/**
 * 套餐与能力模块（平台运营端）
 *
 * 契约里只提供了套餐查询接口（GET /api/platform/plans），没有提供在线编辑，
 * 因此本页定位为**对比与核对视图**：把四个档位的价格、配额、隔离模式、
 * 模块授权放在一张表里逐行对比，便于运营人员答复租户咨询、核对报价口径。
 *
 * 模块授权是这一页的重点：确认某个租户「为什么用不了企查查」时，
 * 先看它所在档位是否包含 qcc 模块，再看租户自身的接口开关，能少绕很多弯路。
 */

import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  ISOLATION_MODE_LABELS,
  PLAN_TIER_LABELS,
  PermissionCode,
  PlanTier,
  type Plan,
  type PlanModules,
} from '@ilc/shared';
import { platformApi } from '@/api/endpoints';
import { mockPlanList } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';

const MODULE_LABELS: Record<keyof PlanModules, { label: string; note: string }> = {
  ocr: { label: '发票识别（OCR）', note: '上传后自动识别票面字段，逐字段给出置信度' },
  qcc: { label: '企查查主体校验', note: '销方存续状态、经营范围匹配、风险信息' },
  taxVerify: { label: '发票验真', note: '核对票面与税务侧记录，可配置进项锁定' },
  tplus: { label: '畅捷通 T+ 对接', note: '业务数据推入 T+ 待办业务池（不生成凭证）' },
  dingtalk: { label: '钉钉待办推送', note: '待办与风险提醒推送到钉钉' },
};

const BILLING_LABELS: Record<Plan['billingMode'], string> = {
  SUBSCRIPTION: '订阅制',
  USAGE: '按调用量',
  MODULE: '模块化授权',
};

const TIER_COLORS: Record<PlanTier, string> = {
  STARTER: 'default',
  STANDARD: 'blue',
  PROFESSIONAL: 'purple',
  ENTERPRISE: 'gold',
};

export default function Plans(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [selectedTier, setSelectedTier] = useState<PlanTier | 'ALL'>('ALL');

  const plans = useResource<Plan[]>(() => platformApi.plans(), { fallback: () => mockPlanList() });

  const list = (plans.data ?? []).filter((p) => selectedTier === 'ALL' || p.tier === selectedTier);

  /** 模块授权矩阵：行=模块，列=档位 */
  const moduleMatrix = Object.keys(MODULE_LABELS) as Array<keyof PlanModules>;
  const tiers = plans.data ?? [];

  return (
    <div className="ilc-page">
      <PageHeader
        title="套餐与能力模块"
        description="四个套餐档位的价格、配额、隔离模式与模块授权对比。租户能使用哪些外部接口，由「套餐模块授权 + 租户接口开关」共同决定。"
        tags={<Tag icon={<CrownOutlined />} color="gold">平台运营</Tag>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={plans.reload}>
            刷新
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="本页为对比视图"
        description="套餐属于平台级基础配置，当前版本由平台统一维护（接口仅提供查询）。如需新增档位或调整价格，请通过配置下发流程处理，变更后此处会自动展示。"
      />

      <div style={{ marginBottom: 12 }}>
        <Segmented
          value={selectedTier}
          onChange={(value) => setSelectedTier(value as PlanTier | 'ALL')}
          options={[
            { value: 'ALL', label: '全部档位' },
            ...Object.entries(PLAN_TIER_LABELS).map(([value, label]) => ({ value, label })),
          ]}
        />
      </div>

      <PageState loading={plans.loading} error={plans.error} onRetry={plans.reload} skeleton rows={8}>
        <Row gutter={[16, 16]}>
          {list.map((plan) => (
            <Col xs={24} md={12} xl={6} key={plan.id}>
              <Card
                size="small"
                title={
                  <Space>
                    <span>{plan.name}</span>
                    <Tag color={TIER_COLORS[plan.tier]}>{PLAN_TIER_LABELS[plan.tier]}</Tag>
                    {!plan.isActive ? <Tag>已下架</Tag> : null}
                  </Space>
                }
                extra={
                  <Tooltip title={`计费周期 ${plan.periodDays} 天 · ${BILLING_LABELS[plan.billingMode]}`}>
                    <Typography.Text strong style={{ fontSize: 16, color: '#cf1322' }}>
                      ¥{(plan.priceCents / 100).toFixed(0)}
                      <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                        {' '}
                        / {plan.periodDays} 天
                      </span>
                    </Typography.Text>
                  </Tooltip>
                }
              >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    {plan.description ?? '（无描述）'}
                  </Typography.Paragraph>

                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="用户上限">
                      <span className="ilc-num">{plan.maxUsers.toLocaleString('zh-CN')}</span> 人
                    </Descriptions.Item>
                    <Descriptions.Item label="月发票上限">
                      <span className="ilc-num">{plan.maxInvoicesPerMonth.toLocaleString('zh-CN')}</span> 张
                    </Descriptions.Item>
                    <Descriptions.Item label="计费方式">{BILLING_LABELS[plan.billingMode]}</Descriptions.Item>
                    <Descriptions.Item label="隔离模式">
                      <Tooltip title="隔离模式决定数据存放位置：共享库靠 tenant_id + 行级安全策略隔离；独立 Schema / 独立库为物理隔离">
                        {ISOLATION_MODE_LABELS[plan.isolationMode]}
                      </Tooltip>
                    </Descriptions.Item>
                  </Descriptions>

                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      能力模块
                    </Typography.Text>
                    <div style={{ marginTop: 4 }}>
                      <Space wrap size={[4, 4]}>
                        {moduleMatrix.map((key) => (
                          <Tag
                            key={key}
                            color={plan.modules[key] ? 'green' : 'default'}
                            icon={plan.modules[key] ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                          >
                            {MODULE_LABELS[key].label}
                          </Tag>
                        ))}
                      </Space>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Card size="small" title="模块授权矩阵（行=能力模块，列=套餐档位）" style={{ marginTop: 16 }}>
          {tiers.length === 0 ? (
            <Typography.Text type="secondary">暂无套餐数据</Typography.Text>
          ) : (
            <Table<{ module: keyof PlanModules }>
              size="small"
              rowKey="module"
              pagination={false}
              dataSource={moduleMatrix.map((module) => ({ module }))}
              columns={[
                {
                  title: '能力模块',
                  dataIndex: 'module',
                  width: 220,
                  fixed: 'left',
                  render: (v: keyof PlanModules) => (
                    <Space direction="vertical" size={0}>
                      <Space size={4}>
                        <ApiOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                        <span>{MODULE_LABELS[v].label}</span>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        {MODULE_LABELS[v].note}
                      </Typography.Text>
                    </Space>
                  ),
                },
                ...tiers.map((plan) => ({
                  title: (
                    <Space direction="vertical" size={0}>
                      <span>{plan.name}</span>
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        ¥{(plan.priceCents / 100).toFixed(0)} / {plan.periodDays} 天
                      </Typography.Text>
                    </Space>
                  ),
                  key: plan.id,
                  align: 'center' as const,
                  width: 130,
                  render: (_v: unknown, record: { module: keyof PlanModules }) =>
                    plan.modules[record.module] ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        包含
                      </Tag>
                    ) : (
                      <Tag icon={<CloseCircleOutlined />}>未包含</Tag>
                    ),
                })),
              ]}
            />
          )}
        </Card>

        <Card size="small" title="配额与模块的关系（答复租户咨询的口径）" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="接口被拒的两种原因">
              ① 套餐未包含该模块（档位问题，需要升级套餐）；② 租户自身把接口关掉了，或月度配额用尽
              （租户配置问题，可在租户的「外部接口配置」中调整）。开通前先分清是哪种，避免误升级。
            </Descriptions.Item>
            <Descriptions.Item label="配额用尽的后果">
              调用会被平台直接拦截并记入调用日志（结果状态「配额拦截」），对应发票的校验项会标记为
              「外部校验暂不可用」，需要租户侧人工核验接管后才能继续流转。因此临近阈值时建议提前升配或调整额度。
            </Descriptions.Item>
            <Descriptions.Item label="隔离模式变更">
              从共享库升级到独立 Schema / 独立库需要数据迁移，属于运维动作，变更前需评估停机窗口；
              套餐变更接口支持按比例折算费用，但不自动执行迁移。
            </Descriptions.Item>
            <Descriptions.Item label="与凭证生成无关">
              <Space size={4}>
                <SafetyCertificateOutlined />
                <span>
                  任何档位都不包含「自动生成会计凭证」的能力：本平台只把业务数据推入 T+ 待办业务池，
                  凭证由财务在 T+ 端选择会计科目生成。
                </span>
              </Space>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </PageState>

      {!can(PermissionCode.TENANT_CONFIG_WRITE) ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="当前账号仅可查看"
          description="套餐变更需要在「租户管理」中针对具体租户执行，并会写入平台审计日志。"
        />
      ) : null}
    </div>
  );
}
